// Aquí están los thunks relacionados con eventos
import { createAsyncThunk } from "@reduxjs/toolkit";
import store from "@/app/store";
import api from "@/services/api";
import {
  eventoSuccess,
  eventoFailure,
  setInformesFiscales,
  setGenerandoInforme,
  setErrorInforme,
} from "./eventoSlice";

/**
 * 📝 EVENTO - Obtener la información de un evento
 */
export const getEventoInfo = createAsyncThunk(
  "evento/getEventoInfo",
  async ({ uidEvento }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/eventos/${uidEvento}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      dispatch(eventoSuccess({ infoEvento: data }));
      return data;      
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al obtener evento"
      );
    }
  }
);

/**
 * 📝 EVENTO - Crear nuevo evento
 */
export const crearEvento = createAsyncThunk(
  "evento/crearEvento",
  async ({ uidEmpresa, eventoData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/eventos/${uidEmpresa}/nuevo-evento`, eventoData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al crear evento"
      );
    }
  }
);

/**
 * 📝 EVENTO - Configurar Evento
 */

export const configHeader = createAsyncThunk(
  "evento/configHeader",
  async ({ uidEvento, configData }, { rejectWithValue, dispatch, getState }) => {
    try {
      // Obtener el estado actual para verificar cambios en la imagen
      const state = getState();
      const currentEvento = state.evento.infoEvento;
      
      // Verificar si hay una imagen nueva (File object) o si se indicó explícitamente que la imagen cambió
      const hasNewImageFile = configData.img instanceof File;
      const imageUpdated = configData.imageUpdated === true;
      
      // Si hay un archivo de imagen nuevo, usar FormData
      if (hasNewImageFile) {
        const formData = new FormData();
        
        // Agregar campos básicos
        if (configData.nombre !== undefined) formData.append('nombre', configData.nombre);
        if (configData.enPromocion !== undefined) formData.append('enPromocion', configData.enPromocion.toString());
        if (configData.textoPromocion !== undefined) formData.append('textoPromocion', configData.textoPromocion);
        if (configData.date !== undefined) formData.append('date', configData.date);
        if (configData.time !== undefined) formData.append('time', configData.time);
        if (configData.dateFin !== undefined) formData.append('dateFin', configData.dateFin);
        if (configData.timeFin !== undefined) formData.append('timeFin', configData.timeFin);
        
        // Agregar flag de imagen actualizada
        formData.append('imageUpdated', 'true');
        
        // Agregar la imagen
        formData.append('img', configData.img); // Cambio de 'imgEvento' a 'img' para consistencia
        
        console.log('Enviando datos con imagen nueva (FormData):', {
          ...configData,
          img: 'File object',
          imageUpdated: true
        });
        
        const { data } = await api.put(`/eventos/${uidEvento}/config-header`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        
        dispatch(eventoSuccess({ infoEvento: data }));
        return data;
      } else {
        // Si no hay imagen nueva, usar JSON
        const dataToSend = { ...configData };
        
        // Incluir el flag imageUpdated para que el backend sepa si debe procesar la imagen
        dataToSend.imageUpdated = imageUpdated;
        
        console.log('Enviando datos (JSON):', {
          ...dataToSend,
          imageUpdated: imageUpdated
        });
        
        const { data } = await api.put(`/eventos/${uidEvento}/config-header`, dataToSend, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        
        dispatch(eventoSuccess({ infoEvento: data }));
        return data;
      }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || "Error al configurar header"
      );
    }
  }
);

export const configSecondaryInfo = createAsyncThunk(
  "evento/configSecondaryInfo",
  async ({ uidEvento, configData }, { rejectWithValue, dispatch }) => {
    try {
      console.log('Enviando datos de información secundaria:', configData);
      
      const { data } = await api.put(`/eventos/${uidEvento}/config-secondary-info`, configData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      dispatch(eventoSuccess({ infoEvento: data }));
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || "Error al configurar información secundaria"
      );
    }
  }
);

export const configTypeTickets = createAsyncThunk(
  "evento/configTypeTickets",
  async ({ uidEvento, ticketsData }, { rejectWithValue, dispatch }) => {
    try {
      console.log('=== DEBUG configTypeTickets ===');
      console.log('Datos recibidos:', ticketsData);
      
      // Verificar si hay archivos de imagen para usar FormData
      const hasImageFiles = ticketsData.some(ticket => {
        const hasFile = ticket.customImage instanceof File;
        console.log(`Ticket ${ticket.customTypeName}: hasFile=${hasFile}, fileName=${ticket.customImage?.name}`);
        return hasFile;
      });
      
      console.log('¿Hay archivos de imagen?', hasImageFiles);
      
      if (hasImageFiles) {
        const formData = new FormData();
        
        // Crear una copia limpia de ticketsData sin los File objects para el JSON
        const cleanTicketsData = ticketsData.map((ticket, index) => {
          const cleanTicket = { ...ticket };
          // Remover el File object del JSON, pero mantener la referencia para saber que tiene imagen custom
          if (cleanTicket.customImage instanceof File) {
            console.log(`Procesando imagen para ticket ${index}: ${cleanTicket.customImage.name}`);
            cleanTicket.hasCustomImageFile = true;
            delete cleanTicket.customImage;
          }
          return cleanTicket;
        });
        
        console.log('Datos limpiados para JSON:', cleanTicketsData);
        
        // Agregar los datos de tickets como JSON string (sin File objects)
        formData.append('ticketsData', JSON.stringify(cleanTicketsData));
        
        // Agregar archivos de imagen individuales con su índice correspondiente
        ticketsData.forEach((ticket, index) => {
          if (ticket.customImage instanceof File) {
            formData.append(`ticketImage_${index}`, ticket.customImage);
            console.log(`✅ Agregando imagen para ticket ${index}:`, ticket.customImage.name, 'Tamaño:', ticket.customImage.size);
          }
        });
        
        console.log('=== Enviando FormData con imágenes ===');
        
        const { data } = await api.put(`/eventos/${uidEvento}/config-type-ticket`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        
        dispatch(eventoSuccess({ infoEvento: data }));
        return data;
      } else {
        console.log('=== Enviando JSON sin imágenes ===');
        // Si no hay archivos, usar JSON
        const { data } = await api.put(`/eventos/${uidEvento}/config-type-ticket`, {
          ticketsData
        }, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        
        dispatch(eventoSuccess({ infoEvento: data }));
        return data;
      }
    } catch (err) {
      console.error('Error en configTypeTickets:', err);
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || "Error al configurar tipos de tickets"
      );
    }
  }
);

export const actualizarUsoEmbajadoresEvento = createAsyncThunk(
  "evento/actualizarUsoEmbajadoresEvento",
  async ({ uidEvento, usoEmbajadores, incentivo }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.put(`/eventos/${uidEvento}/upgrade-use-ambassadors`, {
        usoEmbajadores,
        incentivo
      }, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      dispatch(eventoSuccess({ infoEvento: data }));
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || "Error al actualizar uso de embajadores"
      );
    }
  }
);

/**
 * 📝 EVENTO - Publicar Evento
 */
export const publicarEvento = createAsyncThunk(
  "evento/publicarEvento",
  async ({ uidEvento }, { rejectWithValue, dispatch }) => {
    try {
      console.log('Publicando evento:', uidEvento);
      
      const { data } = await api.put(`/eventos/${uidEvento}/publicar`, {}, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      console.log('Evento publicado exitosamente:', data);
      return data;
    } catch (err) {
      console.error('Error al publicar evento:', err);
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || "Error al publicar evento"
      );
    }
  }
);

/** 
 * * 📝 Evento - Eliminar Evento
*/
export const eliminarEvento = createAsyncThunk(
  "evento/eliminarEvento",
  async ({ uidEvento }, { rejectWithValue, dispatch }) => {
    try {
      console.log('Eliminando evento:', uidEvento);
      
      const { data } = await api.delete(`/eventos/${uidEvento}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      console.log('Evento eliminado exitosamente:', data);
      return data;
    } catch (err) {
      console.error('Error al eliminar evento:', err);
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || "Error al eliminar evento"
      );
    }
  }
);


/**
 * 📝 EVENTO - Dashboard requests
 */
/**
 * Obtener información general y estadísticas del evento
 */
export const getStatsInfoEvento = createAsyncThunk(
  "evento/getStatsInfoEvento",
  async ({ uidEvento }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/eventos/${uidEvento}/stats/info`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      dispatch(eventoSuccess({ statsInfoEvento: data }));
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener información del evento"
      );
    }
  }
)
/**
 * Obtener estadísticas de tipos de tickets vendidos por género
 */
export const getStatsTipoTickets = createAsyncThunk(
  "evento/getStatsTipoTickets",
  async ({ uidEvento }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/eventos/${uidEvento}/stats/tipo-tickets`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      dispatch(eventoSuccess({ statsTipoTickets: data }));
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener estadísticas de tipos de tickets"
      );
    }
  }
)
/**
 * Obtener estadísticas rápidas del evento (validaciones, accesos)
 */
export const getStatsQuickStats = createAsyncThunk(
  "evento/getStatsQuickStats",
  async ({ uidEvento }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/eventos/${uidEvento}/stats/quick-stats`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      dispatch(eventoSuccess({ statsQuickStats: data }));
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener estadísticas rápidas"
      );
    }
  }
)
/**
 * Obtener historial de entradas del evento (con paginación)
 */
export const getStatsHistorialDeEntradas = createAsyncThunk(
  "evento/getStatsHistorialDeEntradas",
  async ({ uidEvento, page = 1, limit = 50 }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/eventos/${uidEvento}/stats/historial-entradas`, {
        params: { page, limit },
        headers: {
          "Content-Type": "application/json",
        },
      });

      dispatch(eventoSuccess({ statsHistorialDeEntradas: data }));
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener historial de entradas"
      );
    }
  }
)
/**
 * Obtener tickets vendidos del evento (con paginación)
 */
export const getStatsTicketsVendidos = createAsyncThunk(
  "evento/getStatsTicketsVendidos",
  async ({ uidEvento, page = 1, limit = 50 }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/eventos/${uidEvento}/stats/tickets-vendidos`, {
        params: { page, limit },
      });
      dispatch(eventoSuccess({ statsTicketsVendidos: data }));
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
/**
 * Obtener estadísticas de embajadores del evento (con paginación)
 */
export const getStatsAmbassadors = createAsyncThunk(
  "evento/getStatsAmbassadors",
  async ({ uidEvento, page = 1, limit = 50 }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/eventos/${uidEvento}/stats/ambassadors`, {
        params: { page, limit },
        headers: {
          "Content-Type": "application/json",
        },
      });

      dispatch(eventoSuccess({ statsAmbassadors: data }));
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener estadísticas de embajadores"
      );
    }
  }
);

/**
 * 📝 EVENTO - Actualizar embajadores activos con links de referencia
 * Activa/desactiva embajadores asignados al evento, gestionando sus referral links
 */
export const actualizarEmbajadoresConLink = createAsyncThunk(
  "evento/actualizarEmbajadoresConLink",
  async ({ uidEvento, embajadoresIds }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.put(
        `/eventos/${uidEvento}/ambassadors/update-links`,
        { embajadoresIds },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // No dispatchar eventoSuccess aquí, se hará un re-fetch para garantizar consistencia
      return data;
    } catch (err) {
      console.error("Error al actualizar embajadores con link:", err);
      return rejectWithValue(
        err.response?.data?.message || "Error al actualizar embajadores del evento"
      );
    }
  }
);

/**
 * Obtener embajadores activos con links de referencia del evento
 */
export const getActiveAmbassadorsForEvent = createAsyncThunk(
  "evento/getActiveAmbassadorsForEvent",
  async ({ uidEvento }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/eventos/${uidEvento}/ambassadors/active`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      dispatch(eventoSuccess({ activeAmbassadors: data.ambassadors }));
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener embajadores activos"
      );
    }
  }
)

/**
 * 📝 EVENTO - Obtener todos los embajadores del evento (paginados)
 * Obtiene la lista completa de embajadores activos con paginación para el modal
 */
export const getAmbassadoresDeEvento = createAsyncThunk(
  "evento/getAmbassadoresDeEvento",
  async ({ uidEvento, page = 1, limit = 6 }, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await api.get(`/eventos/${uidEvento}/ambassadors/all`, {
        params: { page, limit },
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Para paginación: si es página 1, reemplazar; si es > 1, agregar
      dispatch(eventoSuccess({
        ambassadoresEvento: data,
        isFirstPage: page === 1
      }));
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener embajadores del evento"
      );
    }
  }
)

/**
 * 📝 EVENTO - Obtener informes fiscales de un evento
 */
export const fetchInformesFiscales = createAsyncThunk(
  "evento/fetchInformesFiscales",
  async ({ uidEvento }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get(`/eventos/${uidEvento}/informes-fiscales`);
      dispatch(setInformesFiscales(data.informes));
      return data.informes;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener informes fiscales"
      );
    }
  }
);

/**
 * 📝 EVENTO - Generar un nuevo informe fiscal para un evento
 */
export const generarInformeFiscal = createAsyncThunk(
  "evento/generarInformeFiscal",
  async ({ uidEvento }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setGenerandoInforme(true));
      dispatch(setErrorInforme(null));
      const { data } = await api.post(
        `/eventos/${uidEvento}/informes-fiscales/generar`
      );
      dispatch(fetchInformesFiscales({ uidEvento }));
      return data;
    } catch (err) {
      const msg =
        err.response?.data?.message || "Error al generar informe fiscal";
      dispatch(setErrorInforme(msg));
      return rejectWithValue(msg);
    } finally {
      dispatch(setGenerandoInforme(false));
    }
  }
);

/**
 * 📝 EVENTO - Duplicar un evento existente
 */
export const duplicarEvento = createAsyncThunk(
  "evento/duplicarEvento",
  async ({ uidEmpresa, duplicarData }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/eventos/${uidEmpresa}/duplicar-evento`,
        duplicarData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data || "Error al duplicar evento"
      );
    }
  }
);